export const APPEARANCE_STORAGE_KEY = "hb-appearance";

export const APPEARANCE_IDS = ["standard", "cute", "dream", "forest", "sunset", "ember"] as const;
export type Appearance = (typeof APPEARANCE_IDS)[number];

const listeners = new Set<() => void>();

let current: Appearance = "standard";

function isAppearance(v: string): v is Appearance {
  return (APPEARANCE_IDS as readonly string[]).includes(v);
}

function readStored(): Appearance {
  try {
    const v = localStorage.getItem(APPEARANCE_STORAGE_KEY);
    if (v && isAppearance(v) && v !== "standard") return v;
  } catch {
    /* ignore */
  }
  return "standard";
}

function themeColorForAppearance(a: Appearance): string {
  const light = window.matchMedia("(prefers-color-scheme: light)").matches;
  switch (a) {
    case "cute":
      return light ? "#ffd6e8" : "#2a1422";
    case "dream":
      return light ? "#d8e8ff" : "#0c1628";
    case "forest":
      return light ? "#e6f5ec" : "#0d1812";
    case "sunset":
      return light ? "#fff0e6" : "#1a100c";
    case "ember":
      return light ? "#fff2ea" : "#140a08";
    default:
      return "#c8d4e0";
  }
}

function applyThemeColor(content: string) {
  const meta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  if (meta) meta.content = content;
}

/** Sync DOM + meta tag; does not touch storage or listeners. */
export function applyAppearanceToDocument(a: Appearance) {
  if (a === "standard") {
    delete document.documentElement.dataset.appearance;
  } else {
    document.documentElement.dataset.appearance = a;
  }
  applyThemeColor(themeColorForAppearance(a));
}

function notify() {
  for (const fn of listeners) fn();
}

/** Call once before React render to avoid a flash of the wrong preset. */
export function initAppearance() {
  current = readStored();
  applyAppearanceToDocument(current);

  const mq = window.matchMedia("(prefers-color-scheme: light)");
  const onScheme = () => {
    applyThemeColor(themeColorForAppearance(current));
  };
  mq.addEventListener("change", onScheme);
}

export function getAppearance(): Appearance {
  return current;
}

export function setAppearance(a: Appearance) {
  if (a === current) return;
  current = a;
  try {
    if (a === "standard") {
      localStorage.removeItem(APPEARANCE_STORAGE_KEY);
    } else {
      localStorage.setItem(APPEARANCE_STORAGE_KEY, a);
    }
  } catch {
    /* ignore */
  }
  applyAppearanceToDocument(a);
  notify();
}

export function subscribeAppearance(onStoreChange: () => void) {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
}
